import { Attrs, EventListeners, MinzeElement } from 'minze'
import Swiper, { Autoplay, EffectFade, Pagination } from 'swiper'
import styles from 'swiper/swiper-bundle.min.css?inline'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'

const debugMode = false

Locale.getInstance().registerKeySet('fr', 'lvc-home-header', {
  signup: {
    label: 'Créer mon compte',
    href: '/fr/s-inscrire',
  },
  login: {
    label: 'Me connecter',
    href: '/fr/connexion',
  },
  placeholder: {
    smallTitle: 'Bienvenue sur',
    largeTitle: 'La Vitrine Culturelle',
  },
})
Locale.getInstance().registerKeySet('en', 'lvc-home-header', {
  signup: {
    label: 'Create my account',
    href: '/en/signup',
  },
  login: {
    label: 'Log in',
    href: '/en/login',
  },
  placeholder: {
    smallTitle: 'Welcome to',
    largeTitle: 'La Vitrine Culturelle',
  },
})

interface HeaderItem {
  title: {
    fr: string
    en: string
  }
  countText: {
    fr: string
    en: string
  }
}

export interface LvcHomeHeader {
  items: string
  imageUrl: string
  loading: boolean
  recommendedActivityCount: number
}

export class LvcHomeHeader extends MinzeElement {
  attrs: Attrs = ['items']

  imageUrl =
    'https://cdn.prod.website-files.com/6619611492a45d6593c5d2d4/6619611492a45d6593c5d431_lvc-home-charlie%402x.webp'
  parsedItems: HeaderItem[] = []
  user: null | any = null
  recommendedActivityCount = debugMode ? 2048 : -1
  loading = true
  swiper: Swiper | null = null

  onPackResults = (e: CustomEvent) => {
    this.recommendedActivityCount = e.detail.total
    this.generateSlides()
  }
  onAuthChange = () => {
    const auth = MainModule.getInstance().getAuth()
    if (auth.isLoggedIn()) {
      this.user = auth.getUserData()
    } else {
      this.user = null
    }
    this.generateSlides()
  }
  generateSlides = () => {
    // If no user or user with no recommended activities, use the default items
    if (!this.user || (this.user && this.recommendedActivityCount < 0)) {
      this.parsedItems = JSON.parse(decodeURIComponent(debugMode ? testString : this.items))
      if (this.recommendedActivityCount < 0) {
        this.loading = true
      } else {
        this.loading = false
        this.rerender()
      }
    } else {
      // If user is logged in, create a personalized slide with the user's first name
      const fName = this.user?.profile?.personalData?.firstName || ''
      const authSlide = {
        title: {
          fr: `Bonjour, ${fName}`,
          en: `Hello, ${fName}`,
        },
        countText: {
          fr: `${this.recommendedActivityCount || 'Plusieur'} sorties culturelles pourraient vous plaire`,
          en: `There are ${this.recommendedActivityCount || 'many'} cultural events you might like`,
        },
      }
      this.parsedItems = [
        ...[authSlide],
        ...JSON.parse(decodeURIComponent(debugMode ? testString : this.items)),
      ]
      this.loading = false
      this.rerender()
    }
  }
  onReady = () => {
    // Safety to remove loading after 3 seconds
    setTimeout(() => {
      if (!this.loading) return
      this.generateSlides()
      this.loading = false
      this.rerender()
    }, 3000)
  }
  onRender = () => {
    const container = this.select('.home-header__swiper-container') as HTMLElement
    const pagination = this.select('.swiper-pagination') as HTMLElement
    if (!container) return
    if (!pagination) return
    this.swiper = new Swiper(container, {
      modules: [Autoplay, EffectFade, Pagination],
      direction: 'vertical',
      autoHeight: true,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      loop: true,
      allowTouchMove: false,
      pagination: {
        el: pagination,
        clickable: false,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      },
      on: {
        resize: () => {
          this.calibrateSlideHeight()
        },
      },
    })
    this.swiper.updateSlides()
    this.swiper.updateProgress()
    this.calibrateSlideHeight()
  }

  // Handle dynamic height adjustment for the slider container
  calibrateSlideHeight = () => {
    const headerTitles = this.selectAll('.home-header__title') as NodeListOf<HTMLElement>
    const heightList: number[] = []
    headerTitles.forEach((title, index) => {
      heightList.push(title.clientHeight)
    })
    const maxHeight = Math.max(...heightList)
    const container = this.select('.home-header__swiper-container') as HTMLElement
    container.style.height = `${maxHeight + 10}px`
  }

  onButtonClick = async (e: Event) => {
    e.preventDefault()

    const target = e.target as HTMLElement
    const id = target.id

    try {
      if (id === 'home_signup_button') {
        await MainModule.getInstance().analyticsTrack('accueil_cta_creer_mon_compte', {})
      } else if (id === 'home_login_button') {
        await MainModule.getInstance().analyticsTrack('accueil_cta_me_connecter', {})
      }
    } catch (error) {
      console.error('Error tracking button click:', error)
    }

    const href = target.getAttribute('href')
    window.location.href = href || ''
  }

  renderLoader = () => {
    return /*html*/ `
      <div class="home-header__loader-wrapper">
        <div style="width: 45%;" class="home-header__loader-shadow is-small"></div>
        <div style="width: 80%; margin-top: 1rem;" class="home-header__loader-shadow"></div>
        <div style="width: 65%;" class="home-header__loader-shadow"></div>
      </div>
    `
  }
  html = () => {
    return /*html*/ `
    <div class="home-header__component">
      <div class="home-header__image-wrapper">
        <img src="${this.imageUrl}" alt="La Vitrine Culturelle" class="home-header__image" width="" height="">
      </div>
      ${
        this.loading
          ? this.renderLoader()
          : /*html*/ `
        <div>
          ${
            this.parsedItems && this.parsedItems.length > 0
              ? /*html*/ `
              <div class="home-header__swiper-container swiper-no-swiping">
                <div class="swiper-wrapper swiper-no-swiping">
                  ${this.parsedItems
                    .map(
                      item => `
                    <div class="swiper-slide swiper-no-swiping">
                      <h1 class="home-header__title"><span class="home-header__title-small">${item.title[Locale.language]}</span><br>
                      ${item.countText[Locale.language]}</h1>
                    </div>
                  `
                    )
                    .join('')}
                </div>
                <div class="swiper-pagination"></div>
              </div>
              `
              : /*html*/ `
                <h1 class="home-header__title"><span class="home-header__title-small">${Locale.getKey('lvc-home-header.placeholder.smallTitle')}</span><br>
                  ${Locale.getKey('lvc-home-header.placeholder.largeTitle')}
                </h1>
              `
          }
          ${
            this.user
              ? ''
              : /*html*/ `
            <div class="home-header__buttons-wrapper">
              <lvc-link-button id="home_signup_button" href="${Locale.getKey('lvc-home-header.signup.href')}">${Locale.getKey('lvc-home-header.signup.label')}</lvc-link-button>
              <lvc-link-button id="home_login_button" href="${Locale.getKey('lvc-home-header.login.href')}">${Locale.getKey('lvc-home-header.login.label')}</lvc-link-button>
            </div>
          `
          }
          </div>
        </div> `
      }
    </div>
    `
  }
  css = () => `
    ${styles}
    .home-header__loader-wrapper {
      width: 100%;
      height: 300px;
      padding-top: 2.5rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    .home-header__loader-shadow {
      width: 100%;
      background: rgba(0,0,0,0.15);
      border-radius: 999px;
      height: 55px;
    }
    .home-header__loader-shadow.is-small {
      height: 30px;

    }
    .home-header__component {
      display: flex;
      gap: 3rem;
    }
    .home-header__image-wrapper {
      max-width: 290px;
      margin-top: 2rem;
    }
    .home-header__buttons-wrapper {
      margin-top: 2rem;
      display: flex;
      gap: 1rem;
    }
    .home-header__image {
      width: 100%;
      height: auto;
      object-fit: cover;
    }
    .home-header__title {
      font-size: 4.5rem;
      margin: 0;
      line-height: 1.0;
      font-weight: 800;
      padding-right: 2rem;
    }
    .home-header__title-small {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      display: inline-block;

    }
    .home-header__swiper-container {
      height: 300px;
      overflow: hidden;
      position: relative;
      touch-action: pan-y !important;
    }
    .swiper-slide {
      display: flex;
      align-items: flex-start;
    }
    .swiper-pagination-bullet {
      background-color: rgba(255, 255, 255, 0.5);
      transition: background-color 0.2s ease;
    }
    .swiper-pagination-bullet-active {
      background-color: rgba(255, 255, 255, 1);
      transition: background-color 0.2s ease;

    }
    @media screen and (max-width: 1280px) {
      .home-header__title {
        font-size: 3.5rem;
      }
    }
    @media screen and (max-width: 991px) {
      .home-header__swiper-container {
        margin-top: 0;
        padding-right: 0;
      }
      .home-header__image-wrapper {
        margin-top: 0;
      }
      .home-header__buttons-wrapper {
        margin-top: 1rem;
        justify-content: center;
      }
      .home-header__component {
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 1rem;
      }
      .swiper-slide {
        justify-content: center;
        align-items: start;
      }
      .swiper-pagination {
        display: none;
      }
      .home-header__loader-wrapper {
        padding-top: 1rem;
        align-items: center;
        gap: 5px;
      }
      .home-header__loader-shadow {
        height: 40px;
      }
      .home-header__title {
        padding: 0;
      }
      .home-header__loader-shadow.is-small {
        height: 24px;
      }
    }
    @media screen and (max-width: 767px) {
      .home-header__swiper-container {
        height: 175px;
      }
      .home-header__image-wrapper {
        max-width: 200px;
      }
      .home-header__title {
        font-size: 2.5rem;
      }
      .home-header__title-small {
        font-size: 1.25rem;
      }
    }
    @media screen and (max-width: 475px) {
      .home-header__title {
        font-size: 36px;
      }
      .home-header__title-small {
        font-size: 18px;
        font-weight: 800;
        margin-bottom: 0.5rem;
      }
      .home-header__buttons-wrapper {
        margin-top: 0.5rem;
        flex-direction: column;
        align-items: center;
      }
    }
    
  `
  eventListeners: EventListeners = [
    ['#home_signup_button', 'click', this.onButtonClick],
    ['#home_login_button', 'click', this.onButtonClick],
    [window, 'lvc:user_state_changed', this.onAuthChange],
    [window, 'lvc:recommendedPack_results', e => this.onPackResults(e as CustomEvent)],
  ]
}

const testString =
  "%5B%7B%22title%22%3A%7B%22fr%22%3A%22%C3%80%20l'affiche%20%C3%A0%20travers%20le%20Qu%C3%A9bec%22%2C%22en%22%3A%22Showing%20across%20Quebec%22%7D%2C%22countText%22%3A%7B%22fr%22%3A%2211703%20%C3%A9v%C3%A9nements%20culturels%22%2C%22en%22%3A%2211703%20cultural%20events%22%7D%7D%2C%7B%22title%22%3A%7B%22fr%22%3A%22%C3%80%20l'affiche%20%C3%A0%20travers%20le%20Qu%C3%A9bec%22%2C%22en%22%3A%22What's%20on%20across%20Quebec%22%7D%2C%22countText%22%3A%7B%22fr%22%3A%22884%20lieux%20culturels%20anim%C3%A9s%20au%20Qu%C3%A9bec%22%2C%22en%22%3A%22884%20active%20cultural%20venues%20across%20Quebec%22%7D%7D%5D"
